"use client"

import { useCallback } from "react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import {
  ApiErrorResponse,
  ERROR_CODE_TO_I18N_KEY,
  ErrorCode,
} from "@/lib/errors/types"

type ToastPosition =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top-center"
  | "bottom-center"

interface UseApiErrorOptions {
  /** Toast position - defaults to bottom-right for RTL compatibility */
  position?: ToastPosition
  /** Duration in milliseconds - defaults to 5000ms */
  duration?: number
  /** Whether to show toast notifications - defaults to true */
  showToast?: boolean
  /** Custom error mapper for specific error codes */
  errorMapper?: Record<ErrorCode, string>
  /** Callback when error is handled */
  onError?: (error: ApiErrorResponse) => void
}

interface HandleErrorResult {
  /** The final error message (translated) */
  message: string
  /** The error code */
  code: ErrorCode
  /** The HTTP status code */
  statusCode: number
  /** Whether this is a network error */
  isNetworkError: boolean
  /** Whether this is an auth error */
  isAuthError: boolean
}

/**
 * Global API Error Hook
 * Centralizes how errors are interpreted and displayed
 * Integrates with next-intl for translations
 */
export function useApiError(options: UseApiErrorOptions = {}) {
  const {
    position = "top-right",
    duration = 5000,
    showToast = true,
    errorMapper,
    onError,
  } = options

  // Load error translations
  const t = useTranslations("errors")

  /**
   * Get translated error message
   * Priority: custom mapper > i18n key mapping > backend message
   */
  const getErrorMessage = useCallback(
    (error: ApiErrorResponse): string => {
      const { code, message: backendMessage, i18nKey } = error.error

      // 1. Check custom mapper first
      if (errorMapper?.[code]) {
        return errorMapper[code]
      }

      // 2. Try mapped i18n key from error code
      const mappedKey = ERROR_CODE_TO_I18N_KEY[code]
      if (mappedKey) {
        const translated = t(mappedKey.split(".")[1] ?? "serverError")
        // If translation exists (not just returning the key), use it
        if (translated && translated !== mappedKey.split(".")[1]) {
          return translated
        }
      }

      // 3. Try backend-provided i18nKey
      if (i18nKey) {
        const keyParts = i18nKey.split(".")
        if (keyParts.length > 1) {
          const translated = t(keyParts[1] ?? "serverError")
          if (translated && translated !== keyParts[1]) {
            return translated
          }
        }
      }

      // 4. Fallback to backend message
      return backendMessage || t("serverError")
    },
    [t, errorMapper]
  )

  /**
   * Handle API error
   * Returns error info and optionally shows toast
   */
  const handleError = useCallback(
    (error: unknown): HandleErrorResult => {
      // Cast to our error type (it will be in this format from apiClient)
      const apiError = error as ApiErrorResponse

      const code = apiError?.error?.code ?? "INTERNAL_ERROR"
      const statusCode = apiError?.error?.statusCode ?? 500

      // Determine error type
      const isNetworkError = statusCode === 0
      const isAuthError =
        code.startsWith("AUTH_") || statusCode === 401 || statusCode === 403

      // Get translated message
      const message = apiError?.error
        ? getErrorMessage(apiError)
        : t("serverError")

      // Show toast notification
      if (showToast) {
        toast.error(message, {
          position,
          duration,
        })
      }

      // Call callback if provided
      if (onError && apiError?.error) {
        onError(apiError)
      }

      return {
        message,
        code,
        statusCode,
        isNetworkError,
        isAuthError,
      }
    },
    [getErrorMessage, showToast, position, duration, onError, t]
  )

  /**
   * Handle error silently (without toast)
   * Useful when you want to display error inline in form
   */
  const handleErrorSilent = useCallback(
    (error: unknown): HandleErrorResult => {
      const apiError = error as ApiErrorResponse

      const code = apiError?.error?.code ?? "INTERNAL_ERROR"
      const statusCode = apiError?.error?.statusCode ?? 500

      const isNetworkError = statusCode === 0
      const isAuthError =
        code.startsWith("AUTH_") || statusCode === 401 || statusCode === 403

      const message = apiError?.error
        ? getErrorMessage(apiError)
        : t("serverError")

      return {
        message,
        code,
        statusCode,
        isNetworkError,
        isAuthError,
      }
    },
    [getErrorMessage, t]
  )

  /**
   * Check if error is a specific type
   */
  const isErrorCode = useCallback(
    (error: unknown, targetCode: ErrorCode): boolean => {
      const apiError = error as ApiErrorResponse
      return apiError?.error?.code === targetCode
    },
    []
  )

  /**
   * Get field errors from validation error response
   * Useful for form validation errors
   */
  const getFieldErrors = useCallback(
    (error: unknown): Record<string, string> | null => {
      const apiError = error as ApiErrorResponse
      if (apiError?.error?.code !== "VALIDATION_ERROR") {
        return null
      }

      const details = (apiError.error as { details?: Record<string, unknown> })
        .details
      if (!details || typeof details !== "object") {
        return null
      }

      // Extract field errors from details
      const fieldErrors: Record<string, string> = {}
      Object.entries(details).forEach(([field, errorInfo]) => {
        if (typeof errorInfo === "string") {
          fieldErrors[field] = errorInfo
        } else if (typeof errorInfo === "object" && errorInfo !== null) {
          fieldErrors[field] = String(
            (errorInfo as { message?: string }).message ?? t("required")
          )
        }
      })

      return fieldErrors
    },
    [t]
  )

  return {
    handleError,
    handleErrorSilent,
    getErrorMessage,
    isErrorCode,
    getFieldErrors,
    t, // Expose translation function for custom error handling
  }
}

/**
 * Hook to check if user is authenticated
 * Convenience hook for auth error handling
 */
export function useIsAuthError() {
  const { isErrorCode } = useApiError({ showToast: false })

  return {
    isAuthError: (error: unknown) =>
      isErrorCode(error, "AUTH_UNAUTHORIZED") ||
      isErrorCode(error, "AUTH_TOKEN_EXPIRED") ||
      isErrorCode(error, "AUTH_SESSION_EXPIRED"),
    isForbidden: (error: unknown) => isErrorCode(error, "AUTH_FORBIDDEN"),
  }
}
